/**
 * UserDashboard/index.tsx
 *
 * The user's home base. One page that shows every campaign they own, with
 * progress, next-post timing, and status — so they always know what's live,
 * what's in flight, and what they've already shipped before starting another.
 *
 * Routing:
 *   /dashboard                    Lists all campaigns
 *   click card → /campaign-planner?id=X    Deep-link into a specific campaign
 *   "+ New Campaign"  → /campaign-planner  Empty planner
 *
 * Admin-gated: same email allow-list as the campaign planner.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import axios from "axios";

import "../../landing.css";
import {
  pageStyle, navStyle, ACCENT, ACCENT_08, ACCENT_12, spinnerStyle,
} from "../DistillPageV2/sharedStyles";
import type { CampaignSummary, CampaignStatus, SchedulePattern, WeekDay } from "../CampaignPlannerPage/types";

// ── In-progress draft ─────────────────────────────────────────────────────────
// Matches GET /api/drafts list shape (lightweight — no full state blob).

interface UserDraft {
  id:        number;
  title:     string;
  lastStep:  number;   // 1=distilled, 2=post ready, 3=template chosen
  topic:     string | null;
  hook:      string | null;
  createdAt: string;
  updatedAt: string;
}

// ── Individual post (non-campaign distillation) ─────────────────────────────
// Matches the shape returned by GET /api/share/mine.
// Every row IS a share (always has a token); no "un-shared" posts in this list.

interface UserPost {
  token:            string;
  distilledInsight: string;
  topic:            string | null;
  hook:             string | null;
  toneTags:         string[];
  angle:            string | null;
  likes:            number;
  viewCount:        number;
  createdAt:        string;
}

// ── Config ────────────────────────────────────────────────────────────────────

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8085";

const ADMIN_EMAILS = [
  "admin@dkafka.com",
  "vikas@dkafka.com",
  "aditi@dkafka.com",
  "shubham@dkafka.com",
];

// ── Auth helpers (mirrors CampaignPlannerPage) ─────────────────────────────────

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem("distill_auth_token") || localStorage.getItem("distill_guest_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}
function axiosCfg() { return { withCredentials: true, headers: getAuthHeaders() }; }
function getAdminEmail(): string | null {
  const token = localStorage.getItem("distill_auth_token");
  if (!token) return null;
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const payload = JSON.parse(atob(parts[1]));
    return (payload.email as string | undefined) ?? null;
  } catch { return null; }
}
function isAdmin(): boolean {
  const e = getAdminEmail();
  return !!e && ADMIN_EMAILS.includes(e.toLowerCase());
}

function isLoggedIn(): boolean {
  return !!localStorage.getItem("distill_auth_token");
}

// ── Status presentation ────────────────────────────────────────────────────────

interface StatusVisual {
  label:    string;
  color:    string;
  bg:       string;
  pulse?:   boolean;
}

const STATUS_VISUALS: Record<CampaignStatus, StatusVisual> = {
  draft:        { label: 'Draft',      color: '#888',     bg: '#EEEEEC' },
  arc_locked:   { label: 'Arc ready',  color: '#7B5BE8',  bg: 'rgba(123,91,232,0.10)' },
  generating:   { label: 'Generating', color: '#5B4FE8',  bg: ACCENT_12, pulse: true },
  rendering:    { label: 'Rendering',  color: '#5B4FE8',  bg: ACCENT_12, pulse: true },
  locked:       { label: 'Locked',     color: '#5B4FE8',  bg: ACCENT_12 },
  active:       { label: 'Live',       color: '#16A34A',  bg: 'rgba(22,163,74,0.10)', pulse: true },
  paused:       { label: 'Paused',     color: '#D97706',  bg: 'rgba(217,119,6,0.10)' },
  completed:    { label: 'Delivered',  color: '#0EA5E9',  bg: 'rgba(14,165,233,0.10)' },
  cancelled:    { label: 'Cancelled',  color: '#888',     bg: '#EEEEEC' },
};

// ── Pattern label (matches the brief step copy) ────────────────────────────────

function patternLabel(pattern: SchedulePattern, days: WeekDay[] | null): string {
  switch (pattern) {
    case 'daily':           return 'Every day';
    case 'weekdays':        return 'Mon – Fri';
    case 'mwf':             return 'Mon · Wed · Fri';
    case 'tt':              return 'Tue · Thu';
    case 'every_other_day': return 'Every 2 days';
    case 'weekly':          return 'Once a week';
    case 'custom':
      if (!days?.length) return 'Custom';
      return days.map(d => d.charAt(0).toUpperCase() + d.slice(1, 3)).join(' · ');
  }
}

// ── Date formatting ────────────────────────────────────────────────────────────

function relativeNext(iso: string): string {
  try {
    const target = new Date(iso);
    const now    = new Date();
    const diffMs = target.getTime() - now.getTime();
    const diffMin = Math.round(diffMs / 60_000);
    if (diffMin < 0)        return 'now';
    if (diffMin < 60)       return `in ${diffMin} min`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24)        return `in ${diffHr} h`;
    const diffDay = Math.round(diffHr / 24);
    if (diffDay === 1)      return 'tomorrow';
    if (diffDay < 7)        return `in ${diffDay} days`;
    return target.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ''; }
}

function shortTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
}

// ── Filter buckets ─────────────────────────────────────────────────────────────

type FilterKey = 'all' | 'live' | 'paused' | 'done' | 'drafts';

const FILTER_LABELS: { key: FilterKey; label: string; matches: (c: CampaignSummary) => boolean }[] = [
  { key: 'all',    label: 'All',       matches: () => true },
  { key: 'live',   label: 'Live',      matches: c => c.status === 'active' },
  { key: 'paused', label: 'Paused',    matches: c => c.status === 'paused' },
  { key: 'done',   label: 'Delivered', matches: c => c.status === 'completed' },
  { key: 'drafts', label: 'Drafts',    matches: c => ['draft','arc_locked','generating','rendering','locked'].includes(c.status) },
];

// ── Component ──────────────────────────────────────────────────────────────────

const UserDashboard: React.FC = () => {
  const history = useHistory();
  const admin   = isAdmin();
  const [campaigns, setCampaigns] = useState<CampaignSummary[] | null>(admin ? null : []);
  const [posts,     setPosts]     = useState<UserPost[]    | null>(null);
  const [drafts,    setDrafts]    = useState<UserDraft[]   | null>(null);
  const [error,     setError]     = useState<string | null>(null);
  const [filter,    setFilter]    = useState<FilterKey>('all');

  // Fetch both in parallel on mount.
  // Campaigns: admin-only — silently ignored for non-admins (403 → empty list).
  // Posts: all insights for this user/install, regardless of share status.
  useEffect(() => {
    if (!isLoggedIn()) return;

    if (admin) {
      axios.get<{ campaigns: CampaignSummary[] }>(`${API_URL}/api/campaigns`, axiosCfg())
        .then(r => setCampaigns(r.data.campaigns ?? []))
        .catch(err => {
          if (err?.response?.status !== 403) {
            setError(err?.response?.data?.error ?? 'Failed to load campaigns');
          } else {
            setCampaigns([]);
          }
        });
    }

    // Pass install_id as fallback so posts created while guest (no user_id stored)
    // are still fetched when the user later logs in.
    const installId = localStorage.getItem('distill_install_id') || localStorage.getItem('install_id') || '';
    const installQs = installId ? `?install_id=${encodeURIComponent(installId)}` : '';

    // GET /api/share/mine — returns posts the user published via DistillPageV2.
    // Every row has a token (they ARE shares); newest first.
    axios.get<UserPost[]>(`${API_URL}/api/share/mine${installQs}`, axiosCfg())
      .then(r => setPosts(r.data ?? []))
      .catch(() => setPosts([]));

    // Drafts — in-progress DistillPageV2 sessions not yet published
    axios.get<UserDraft[]>(`${API_URL}/api/drafts${installQs}`, axiosCfg())
      .then(r => setDrafts(r.data ?? []))
      .catch(() => setDrafts([]));
  }, [admin]);

  // Hero stats — combines campaigns + individual posts so the count feels right.
  const stats = useMemo(() => {
    const camps = campaigns ?? [];
    const indiv = posts ?? [];
    // All rows from /api/share/mine are shares — every item has a token.
    return {
      // "Published" = campaign posts that hit LinkedIn + individually shared posts
      published: camps.reduce((sum, c) => sum + c.publishedPosts, 0) + indiv.length,
      pending:   camps.reduce((sum, c) => sum + c.pendingPosts, 0),
      live:      camps.filter(c => c.status === 'active').length,
      paused:    camps.filter(c => c.status === 'paused').length,
      posts:     indiv.length,
    };
  }, [campaigns, posts]);

  const filtered = useMemo(() => {
    const fn = FILTER_LABELS.find(f => f.key === filter)?.matches ?? (() => true);
    return (campaigns ?? []).filter(fn);
  }, [campaigns, filter]);

  // Greeting tuned to the user's actual state — nothing generic.
  const greeting = useMemo(() => {
    if (!campaigns || !posts) return null;
    const totalCamps = campaigns.length;
    const totalPosts = posts.length;

    // Mostly-empty new user
    if (totalCamps === 0 && totalPosts === 0) {
      return { title: "Your stage is set.", sub: "Ready when you are." };
    }
    // Active campaigns running — celebrate momentum
    if (stats.live > 0) {
      return { title: "Your campaigns are live.", sub: `${stats.live} running · keep the momentum.` };
    }
    // Paused — gentle nudge
    if (stats.paused > 0) {
      return { title: "Picking up where you left off?", sub: `${stats.paused} paused · resume any time.` };
    }
    // Lots of posts shipped — make them feel proud
    if (stats.published >= 10) {
      return { title: `${stats.published} posts shipped.`, sub: "Quietly building authority. Keep going." };
    }
    if (stats.published > 0) {
      return { title: `${stats.published} ${stats.published === 1 ? 'post' : 'posts'} shipped.`, sub: "Time to start the next one?" };
    }
    return { title: "Welcome back.", sub: "Let's get the next idea moving." };
  }, [campaigns, posts, stats]);

  // ── Logged-out gate ────────────────────────────────────────────────────────
  if (!isLoggedIn()) {
    return (
      <div style={pageStyle}>
        <DashNav />
        <div style={{ maxWidth: 460, margin: '80px auto', textAlign: 'center', padding: '0 24px' }}>
          <div style={{ fontSize: 36, marginBottom: 16 }}>🔒</div>
          <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>Sign in to see your dashboard</div>
          <div style={{ fontSize: 14, color: '#888', marginBottom: 24 }}>
            Your campaigns and posts live behind a login.
          </div>
          <Link to="/distill-v2" style={ghostLink}>← Back to App</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <DashNav />

      <main style={{ maxWidth: 1100, margin: '0 auto', padding: '40px 28px 80px' }}>

        {/* ── Hero ─────────────────────────────────────────────────────────── */}
        {greeting && (
          <div style={{ marginBottom: 32 }}>
            <h1 style={{ fontSize: 36, fontWeight: 800, letterSpacing: '-0.04em', margin: '0 0 6px' }}>
              {greeting.title}
            </h1>
            <p style={{ fontSize: 16, color: '#888', margin: 0 }}>{greeting.sub}</p>
          </div>
        )}

        {/* ── Stats strip ──────────────────────────────────────────────────── */}
        {(stats.published > 0 || (campaigns?.length ?? 0) > 0) && (
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: 12, marginBottom: 28,
          }}>
            <Stat label="Published" value={stats.published} hint="posts shared" />
            {admin && <Stat label="Scheduled" value={stats.pending} hint="going out next" />}
            {admin && <Stat label="Live"      value={stats.live}    hint="campaigns running" />}
            {!admin && <Stat label="Posts"    value={stats.posts}   hint="in your archive" />}
            <Stat
              label={admin ? "Campaigns" : "All time"}
              value={admin ? (campaigns?.length ?? 0) : stats.published}
              hint={admin ? "total" : "posts"}
            />
          </div>
        )}

        {/* ── Primary CTA — admins start a campaign, others start a post ──── */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 32 }}>
          {admin && (
            <Link
              to="/campaign-planner"
              data-testid="ud-new-campaign"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: ACCENT, color: '#fff',
                padding: '12px 22px', borderRadius: 12,
                fontSize: 14, fontWeight: 700, textDecoration: 'none',
                fontFamily: 'inherit', letterSpacing: '-0.01em',
                boxShadow: '0 4px 14px rgba(91,79,232,0.25)',
                transition: 'transform 120ms, box-shadow 120ms',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(91,79,232,0.30)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)';   e.currentTarget.style.boxShadow = '0 4px 14px rgba(91,79,232,0.25)'; }}
            >
              <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
              New Campaign
            </Link>
          )}
          <Link
            to="/distill-v2"
            data-testid="ud-new-post"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: admin ? '#fff'   : ACCENT,
              color:      admin ? ACCENT   : '#fff',
              padding: '12px 22px', borderRadius: 12,
              border: `1.5px solid ${ACCENT}`,
              fontSize: 14, fontWeight: 700, textDecoration: 'none',
              fontFamily: 'inherit', letterSpacing: '-0.01em',
              boxShadow: admin ? 'none' : '0 4px 14px rgba(91,79,232,0.25)',
            }}
          >
            <span style={{ fontSize: 18, lineHeight: 1 }}>✦</span>
            New Post
          </Link>
        </div>

        {/* ── Loading / global error ───────────────────────────────────────── */}
        {error && (
          <div style={{
            background: '#FFF0F0', border: '1.5px solid #E84F4F',
            borderRadius: 12, padding: '14px 16px',
            color: '#C0392B', fontSize: 14, marginBottom: 24,
          }}>
            {error}
          </div>
        )}

        {!error && (campaigns === null || posts === null || drafts === null) && <LoadingState />}

        {/* Both empty → unified empty state */}
        {!error && campaigns?.length === 0 && posts?.length === 0 && !drafts?.length && (
          <EmptyState admin={admin} />
        )}

        {/* ── Section: In-progress drafts ──────────────────────────────────── */}
        {!error && (drafts?.length ?? 0) > 0 && (
          <DraftsSection
            drafts={drafts!}
            onDelete={id => {
              axios.delete(`${API_URL}/api/drafts/${id}`, axiosCfg()).catch(() => {});
              setDrafts(prev => (prev ?? []).filter(d => d.id !== id));
            }}
          />
        )}

        {/* ── Section: Campaigns (admin only when any exist) ───────────────── */}
        {!error && admin && (campaigns?.length ?? 0) > 0 && (
          <section style={{ marginBottom: 40 }}>
            <SectionHeader
              title="Your campaigns"
              count={campaigns!.length}
              hint="Multi-day LinkedIn arcs"
            />
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '12px 0 16px' }}>
              {FILTER_LABELS.map(f => {
                const count  = (campaigns ?? []).filter(f.matches).length;
                const active = filter === f.key;
                return (
                  <button
                    key={f.key}
                    onClick={() => setFilter(f.key)}
                    data-testid={`ud-filter-${f.key}`}
                    style={{
                      background: active ? ACCENT : '#fff',
                      color:      active ? '#fff' : '#444',
                      border:     `1.5px solid ${active ? ACCENT : '#E4E4E0'}`,
                      padding:    '6px 13px', borderRadius: 8,
                      fontSize:   13, fontWeight: 600, fontFamily: 'inherit',
                      cursor:     'pointer',
                      display:    'inline-flex', alignItems: 'center', gap: 6,
                    }}
                  >
                    {f.label}
                    <span style={{ fontSize: 11, fontWeight: 700, opacity: active ? 0.85 : 0.55 }}>{count}</span>
                  </button>
                );
              })}
            </div>

            {filtered.length === 0 ? (
              <div style={{ padding: '32px 0', textAlign: 'center', color: '#888', fontSize: 14 }}>
                No campaigns in this view.
              </div>
            ) : (
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                gap: 16,
              }}>
                {filtered.map(c => (
                  <CampaignCard
                    key={c.id}
                    campaign={c}
                    onClick={() => history.push(`/campaign-planner?id=${c.id}`)}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        {/* ── Section: Individual posts (everyone) ─────────────────────────── */}
        {!error && (posts?.length ?? 0) > 0 && (
          <section>
            <SectionHeader
              title="Your posts"
              count={posts!.length}
              hint="Every idea you've distilled"
            />
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 14, marginTop: 16,
            }}>
              {posts!.map(p => <PostCard key={p.token} post={p} />)}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────

const DashNav: React.FC = () => (
  <nav style={navStyle}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <Link
        to="/distill-v2"
        title="Back to distill v2"
        data-testid="ud-back-to-app"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          textDecoration: 'none', color: ACCENT, background: '#fff',
          border: `1.5px solid ${ACCENT}`, borderRadius: 8,
          padding: '6px 12px', fontSize: 13, fontWeight: 700,
          fontFamily: 'inherit',
        }}
      >
        <span style={{ fontSize: 15, lineHeight: 1 }}>←</span>
        Back to App
      </Link>
      <Link to="/distill-v2" style={{ textDecoration: 'none', color: 'inherit' }}>
        <span style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, fontSize: 18 }}>
          distill
          <sup style={{ fontSize: 8, letterSpacing: '0.1em', fontWeight: 600, color: ACCENT, verticalAlign: 'super', marginLeft: 2, textTransform: 'uppercase' }}>
            v2
          </sup>
        </span>
      </Link>
      <span style={{ color: '#E4E4E0', fontWeight: 300 }}>|</span>
      <span style={{ fontSize: 14, fontWeight: 700, color: '#444' }}>Dashboard</span>
    </div>
    {/* Rollback: remove this Link to revert the nav change */}
    <Link
      to="/voice-profile"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        textDecoration: 'none', color: '#555',
        fontSize: 13, fontWeight: 600,
        padding: '6px 12px', borderRadius: 8,
        background: ACCENT_08,
        fontFamily: 'inherit',
        transition: 'background 0.15s',
      }}
      title="Manage your voice profile"
    >
      ✍️ Voice Profile
    </Link>
  </nav>
);

const Stat: React.FC<{ label: string; value: number; hint: string }> = ({ label, value, hint }) => (
  <div style={{
    background: '#fff', border: '1.5px solid #E4E4E0', borderRadius: 12,
    padding: '16px 18px',
  }}>
    <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1 }}>
      {value}
    </div>
    <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#444', marginTop: 4 }}>
      {label}
    </div>
    <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{hint}</div>
  </div>
);

const LoadingState: React.FC = () => (
  <div style={{ padding: '40px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
    <span style={spinnerStyle} />
    <span style={{ fontSize: 13, color: '#888' }}>Loading your campaigns…</span>
  </div>
);

interface EmptyStateProps { admin: boolean; }
const EmptyState: React.FC<EmptyStateProps> = ({ admin }) => (
  <div style={{
    border: '2px dashed #E4E4E0', borderRadius: 16,
    padding: '60px 24px', textAlign: 'center',
    background: '#fff',
  }}>
    <div style={{ fontSize: 40, marginBottom: 14 }}>✨</div>
    <h2 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.03em', margin: '0 0 6px' }}>
      A blank stage. A waiting audience.
    </h2>
    <p style={{ fontSize: 14, color: '#888', maxWidth: 460, margin: '0 auto 20px' }}>
      {admin
        ? "Your first idea is one click away. Distill a single post, or build a multi-day campaign on autopilot."
        : "Your first idea is one click away. Distill it into a LinkedIn-ready post in seconds."}
    </p>
    <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
      <Link
        to="/distill-v2"
        style={{
          display: 'inline-block', background: ACCENT, color: '#fff',
          padding: '12px 26px', borderRadius: 12,
          fontSize: 14, fontWeight: 700, textDecoration: 'none',
          fontFamily: 'inherit',
        }}
      >
        Start your first post →
      </Link>
      {admin && (
        <Link
          to="/campaign-planner"
          style={{
            display: 'inline-block', background: '#fff', color: ACCENT,
            border: `1.5px solid ${ACCENT}`,
            padding: '12px 26px', borderRadius: 12,
            fontSize: 14, fontWeight: 700, textDecoration: 'none',
            fontFamily: 'inherit',
          }}
        >
          Or plan a campaign
        </Link>
      )}
    </div>
  </div>
);

// ── Section header (re-used by both Campaigns and Posts sections) ──────────────

const SectionHeader: React.FC<{ title: string; count: number; hint: string }> = ({ title, count, hint }) => (
  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
    <h2 style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.03em', margin: 0 }}>
      {title}
    </h2>
    <span style={{
      background: ACCENT_08, color: ACCENT,
      borderRadius: 6, padding: '2px 8px',
      fontSize: 12, fontWeight: 700,
    }}>{count}</span>
    <span style={{ fontSize: 13, color: '#888', marginLeft: 'auto' }}>{hint}</span>
  </div>
);

// ── Campaign card ─────────────────────────────────────────────────────────────

interface CardProps {
  campaign: CampaignSummary;
  onClick:  () => void;
}

const CampaignCard: React.FC<CardProps> = ({ campaign: c, onClick }) => {
  const v = STATUS_VISUALS[c.status];
  const pct = c.totalPosts > 0 ? Math.round(100 * c.publishedPosts / c.totalPosts) : 0;

  // Footnote chooses what's most useful right now:
  //  active   → next post timing
  //  paused   → reassuring nudge
  //  done     → celebrate
  //  default  → neutral
  let footnote: { text: string; tone: 'normal' | 'success' | 'warn' | 'live' };
  if (c.status === 'active' && c.nextPostAt) {
    footnote = { text: `Next post ${relativeNext(c.nextPostAt)} · ${shortTime(c.nextPostAt)}`, tone: 'live' };
  } else if (c.status === 'paused') {
    footnote = { text: `${c.publishedPosts} of ${c.totalPosts} done · paused, ready to resume`, tone: 'warn' };
  } else if (c.status === 'completed') {
    footnote = { text: `All ${c.totalPosts} delivered ✓`, tone: 'success' };
  } else if (c.status === 'generating' || c.status === 'rendering') {
    footnote = { text: 'Crafting captions and slides…', tone: 'normal' };
  } else if (c.status === 'arc_locked' || c.status === 'draft') {
    footnote = { text: 'Arc ready — open to lock and schedule', tone: 'normal' };
  } else {
    footnote = { text: '', tone: 'normal' };
  }

  const toneColor = footnote.tone === 'success' ? '#16A34A'
                  : footnote.tone === 'warn'    ? '#D97706'
                  : footnote.tone === 'live'    ? ACCENT
                  : '#888';

  return (
    <button
      onClick={onClick}
      data-testid={`ud-campaign-card-${c.id}`}
      style={{
        background: '#fff', border: '1.5px solid #E4E4E0', borderRadius: 14,
        padding: '18px 20px',
        textAlign: 'left', fontFamily: 'inherit', cursor: 'pointer',
        display: 'flex', flexDirection: 'column', gap: 12,
        position: 'relative', overflow: 'hidden',
        transition: 'transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform   = 'translateY(-2px)';
        e.currentTarget.style.boxShadow   = '0 6px 22px rgba(0,0,0,0.06)';
        e.currentTarget.style.borderColor = '#D6D4CE';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform   = 'translateY(0)';
        e.currentTarget.style.boxShadow   = 'none';
        e.currentTarget.style.borderColor = '#E4E4E0';
      }}
    >
      {/* Status stripe on left */}
      <div style={{
        position: 'absolute', left: 0, top: 0, bottom: 0, width: 3,
        background: v.color,
      }} />

      {/* Top: status + pattern chip */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          background: v.bg, color: v.color,
          padding: '3px 9px', borderRadius: 6,
          fontSize: 11, fontWeight: 700, letterSpacing: '0.02em', textTransform: 'uppercase',
        }}>
          <span style={{
            width: 6, height: 6, borderRadius: '50%', background: v.color,
            boxShadow: v.pulse ? `0 0 0 0 ${v.color}` : undefined,
            animation:  v.pulse ? 'ud-pulse 1.6s infinite' : undefined,
          }} />
          {v.label}
        </span>
        <span style={{
          background: '#F4F4F0', color: '#666',
          padding: '3px 9px', borderRadius: 6,
          fontSize: 11, fontWeight: 600,
        }}>
          {patternLabel(c.schedulePattern, c.scheduleDays)}
        </span>
      </div>

      {/* Title */}
      <div>
        <div style={{
          fontSize: 16, fontWeight: 800, letterSpacing: '-0.02em',
          marginBottom: 4, lineHeight: 1.3,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {c.name}
        </div>
        <div style={{
          fontSize: 13, color: '#888', lineHeight: 1.4,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}>
          {c.topic}
        </div>
      </div>

      {/* Progress bar — only meaningful when posts exist */}
      {c.totalPosts > 0 && (
        <div>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
            fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 6,
          }}>
            <span>{c.publishedPosts} of {c.totalPosts} published</span>
            <span style={{ color: '#aaa' }}>{pct}%</span>
          </div>
          <div style={{
            height: 6, background: '#F0F0EC', borderRadius: 3, overflow: 'hidden',
          }}>
            <div style={{
              width: `${pct}%`, height: '100%', background: v.color,
              transition: 'width 400ms ease',
            }} />
          </div>
        </div>
      )}

      {/* Footnote */}
      {footnote.text && (
        <div style={{ fontSize: 12, color: toneColor, fontWeight: 600 }}>
          {footnote.text}
        </div>
      )}

      {/* Inline keyframes for the pulse — local to the card so it doesn't leak. */}
      <style>{`
        @keyframes ud-pulse {
          0%   { box-shadow: 0 0 0 0   rgba(22,163,74,0.50); }
          70%  { box-shadow: 0 0 0 6px rgba(22,163,74,0);    }
          100% { box-shadow: 0 0 0 0   rgba(22,163,74,0);    }
        }
      `}</style>
    </button>
  );
};

// ── Individual post card ──────────────────────────────────────────────────────

const PostCard: React.FC<{ post: UserPost }> = ({ post }) => {
  const dateStr = (() => {
    try {
      const d = new Date(post.createdAt);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return ''; }
  })();

  // Body preview: hook is the punchy first line, fall back to insight
  const headline = (post.hook?.trim() || post.distilledInsight).slice(0, 140);
  const body     = post.distilledInsight;

  // All rows from /api/share/mine are published — every card links to its share page.
  const cardStyle: React.CSSProperties = {
    background: '#fff', border: '1.5px solid #E4E4E0', borderRadius: 14,
    padding: '16px 18px',
    textDecoration: 'none', color: 'inherit', fontFamily: 'inherit',
    display: 'flex', flexDirection: 'column', gap: 10,
    position: 'relative', overflow: 'hidden',
    transition: 'transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease',
  };
  const hoverOn  = (el: HTMLElement) => {
    el.style.transform   = 'translateY(-2px)';
    el.style.boxShadow   = '0 6px 22px rgba(0,0,0,0.06)';
    el.style.borderColor = '#D6D4CE';
  };
  const hoverOff = (el: HTMLElement) => {
    el.style.transform   = 'translateY(0)';
    el.style.boxShadow   = 'none';
    el.style.borderColor = '#E4E4E0';
  };

  const inner = (
    <>
      {/* Cool-tone stripe on the left */}
      <div style={{
        position: 'absolute', left: 0, top: 0, bottom: 0, width: 3,
        background: '#0EA5E9',
      }} />

      {/* Top row: topic chip + date */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
        {post.topic ? (
          <span style={{
            background: '#F4F4F0', color: '#666',
            padding: '3px 9px', borderRadius: 6,
            fontSize: 11, fontWeight: 600,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            maxWidth: 160,
          }}>
            {post.topic}
          </span>
        ) : <span />}
        <span style={{ fontSize: 11, color: '#999', fontWeight: 600, flexShrink: 0 }}>
          {dateStr}
        </span>
      </div>

      {/* Headline (hook) */}
      <div style={{
        fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em',
        lineHeight: 1.4, color: '#0A0A0A',
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}>
        {headline}
      </div>

      {/* Body preview */}
      <div style={{
        fontSize: 13, color: '#666', lineHeight: 1.5,
        display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}>
        {body}
      </div>

      {/* Footer — share stats + link cue */}
      <div style={{
        marginTop: 'auto', paddingTop: 4,
        fontSize: 12, fontWeight: 700,
        color: '#0EA5E9',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <span>Open share page <span style={{ fontSize: 13 }}>→</span></span>
        {post.likes > 0 && (
          <span style={{ color: '#AAA', fontWeight: 600 }}>
            ♥ {post.likes}
          </span>
        )}
      </div>
    </>
  );

  return (
    <Link
      to={`/share/${post.token}`}
      data-testid={`ud-post-card-${post.token}`}
      style={cardStyle}
      onMouseEnter={e => hoverOn(e.currentTarget as HTMLElement)}
      onMouseLeave={e => hoverOff(e.currentTarget as HTMLElement)}
    >
      {inner}
    </Link>
  );
};

const ghostLink: React.CSSProperties = {
  display:        'inline-block',
  background:     ACCENT,
  color:          '#fff',
  padding:        '10px 18px',
  borderRadius:   10,
  fontSize:       14,
  fontWeight:     700,
  textDecoration: 'none',
  fontFamily:     'inherit',
};

// ── Drafts section ────────────────────────────────────────────────────────────

const STEP_LABELS: Record<number, { label: string; color: string; bg: string }> = {
  1: { label: 'Insight ready',   color: '#7B5BE8', bg: 'rgba(123,91,232,0.10)' },
  2: { label: 'Post written',    color: '#0EA5E9', bg: 'rgba(14,165,233,0.10)' },
  3: { label: 'Template chosen', color: '#16A34A', bg: 'rgba(22,163,74,0.10)'  },
};

interface DraftsSectionProps {
  drafts:   UserDraft[];
  onDelete: (id: number) => void;
}

const DraftsSection: React.FC<DraftsSectionProps> = ({ drafts, onDelete }) => (
  <section style={{ marginBottom: 36 }}>
    <SectionHeader
      title="In progress"
      count={drafts.length}
      hint="Continue where you left off"
    />
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
      gap: 12, marginTop: 16,
    }}>
      {drafts.map(d => (
        <DraftCard key={d.id} draft={d} onDelete={() => onDelete(d.id)} />
      ))}
    </div>
  </section>
);

interface DraftCardProps {
  draft:    UserDraft;
  onDelete: () => void;
}

const DraftCard: React.FC<DraftCardProps> = ({ draft, onDelete }) => {
  const [confirmDelete, setConfirmDelete] = React.useState(false);

  const stepVis = STEP_LABELS[draft.lastStep] ?? STEP_LABELS[1];

  const age = (() => {
    try {
      const ms  = Date.now() - new Date(draft.updatedAt).getTime();
      const min = Math.round(ms / 60_000);
      if (min < 2)   return 'just now';
      if (min < 60)  return `${min} min ago`;
      const hr = Math.round(min / 60);
      if (hr < 24)   return `${hr} h ago`;
      const d = Math.round(hr / 24);
      return d === 1 ? 'yesterday' : `${d} days ago`;
    } catch { return ''; }
  })();

  const headline = draft.hook?.trim() || draft.title;

  return (
    <div
      data-testid={`ud-draft-card-${draft.id}`}
      style={{
        background: '#FAFAF8', border: '1.5px dashed #D4D0C8', borderRadius: 14,
        padding: '14px 16px',
        display: 'flex', flexDirection: 'column', gap: 8,
        position: 'relative', overflow: 'hidden',
      }}
    >
      {/* Amber left stripe — visually distinct from published cards */}
      <div style={{
        position: 'absolute', left: 0, top: 0, bottom: 0, width: 3,
        background: '#F59E0B',
      }} />

      {/* Top row: step badge + age */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{
          background: stepVis.bg, color: stepVis.color,
          padding: '3px 8px', borderRadius: 6,
          fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
        }}>
          {stepVis.label}
        </span>
        <span style={{ fontSize: 11, color: '#AAA', fontWeight: 600, flexShrink: 0 }}>
          {age}
        </span>
      </div>

      {/* Headline */}
      <div style={{
        fontSize: 13, fontWeight: 700, letterSpacing: '-0.01em',
        lineHeight: 1.4, color: '#0A0A0A',
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}>
        {headline}
      </div>

      {/* Actions row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
        <Link
          to={`/distill-v2?draft=${draft.id}`}
          style={{
            flex: 1,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
            background: ACCENT, color: '#fff',
            padding: '7px 12px', borderRadius: 8,
            fontSize: 12, fontWeight: 700, textDecoration: 'none',
            fontFamily: 'inherit',
          }}
        >
          Continue <span style={{ fontSize: 14 }}>→</span>
        </Link>

        {/* Delete — two-tap confirm so accidental taps don't wipe work */}
        {confirmDelete ? (
          <button
            onClick={onDelete}
            style={{
              background: '#FFF0F0', color: '#E84F4F',
              border: '1.5px solid #E84F4F',
              padding: '7px 10px', borderRadius: 8,
              fontSize: 12, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Delete?
          </button>
        ) : (
          <button
            onClick={() => setConfirmDelete(true)}
            title="Discard draft"
            style={{
              background: 'transparent', color: '#BBB',
              border: '1.5px solid #E4E4E0',
              padding: '7px 10px', borderRadius: 8,
              fontSize: 13, fontFamily: 'inherit', cursor: 'pointer',
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
};

export default UserDashboard;
